/**
 * LeakScanMap — Leaflet map that plots every scan location as a marker.
 *
 * Rendered inside the "Where viewed" tab on the Leak-trace ledger. Each
 * marker shows a popup with the licensee's email, the scan timestamp,
 * the referer, and any blocklist match.
 *
 * Leaflet CSS is loaded via CDN link tag so we don't have to touch the
 * global `index.css` bundle configuration.
 */

import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import L from 'leaflet';

// Fix the default marker icon path (webpack strips it in production builds).
const DEFAULT_ICON = new L.Icon({
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize:    [25, 41],
  iconAnchor:  [12, 41],
  popupAnchor: [1, -34],
  shadowSize:  [41, 41],
});

const CSS_HREF = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';

const LeakScanMap = ({ scans, height = 400 }) => {
  // Inject the Leaflet stylesheet once — safe to re-run because we check
  // for the link tag before adding it.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.querySelector(`link[href="${CSS_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CSS_HREF;
    document.head.appendChild(link);
  }, []);

  const points = (scans || []).filter(
    (s) => s.geo && typeof s.geo.lat === 'number' && typeof s.geo.lon === 'number'
  );

  return (
    <div className="rounded-xl overflow-hidden border border-border" data-testid="leak-scan-map">
      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height, width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.map((s) => {
          const blocked = !!s.blocklist_hit;
          return (
            <React.Fragment key={s.id}>
              {/* Halo — larger red circle for blocklist hits, primary for normal */}
              <CircleMarker
                center={[s.geo.lat, s.geo.lon]}
                radius={blocked ? 12 : 8}
                pathOptions={{
                  color:      blocked ? '#dc2626' : '#2563eb',
                  fillColor:  blocked ? '#dc2626' : '#3b82f6',
                  fillOpacity: 0.35,
                  weight: 1,
                }}
              />
              <Marker position={[s.geo.lat, s.geo.lon]} icon={DEFAULT_ICON}>
                <Popup>
                  <div className="text-xs space-y-1" style={{ minWidth: 200 }}>
                    <div><strong>{s.email}</strong></div>
                    <div style={{ fontFamily: 'ui-monospace, monospace' }}>{s.token}</div>
                    <div>{new Date(s.created_at).toLocaleString()}</div>
                    <div>
                      {s.geo.city ? `${s.geo.city}, ` : ''}{s.geo.country} · {s.geo.isp || 'unknown ISP'}
                    </div>
                    {s.referer && (
                      <div style={{ wordBreak: 'break-all', color: blocked ? '#b91c1c' : undefined }}>
                        <strong>Referer:</strong>{' '}
                        <a href={s.referer} target="_blank" rel="noopener noreferrer">{s.referer}</a>
                      </div>
                    )}
                    {blocked && (
                      <div style={{ color: '#b91c1c', fontWeight: 700 }}>
                        BLOCKLIST: {s.blocklist_hit}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            </React.Fragment>
          );
        })}
      </MapContainer>
      {points.length === 0 && (
        <div className="p-4 text-xs text-muted-foreground text-center">
          No geolocated scans yet. Scans from internal / private IPs don&rsquo;t plot on the map.
        </div>
      )}
    </div>
  );
};

export default LeakScanMap;
